"use client";

import { useAsRef } from "@/hooks/use-as-ref";
import { subscribeToEventStream } from "@/lib/event-stream";
import type { HandyoutProcessingStatus } from "@/types/handyout";
import type { TranslationStatus } from "@/types/translations";
import { useEffect } from "react";

export interface HandyoutEvent {
  type: string;
  slide_id?: string;
  handyout_id?: string;
  processing_status?: HandyoutProcessingStatus;
  version?: number;
  code?: string;
  status?: TranslationStatus;
}

export interface HandyoutEventSubscriber {
  onEvent: (event: HandyoutEvent) => void;
  onResync?: () => void;
}

export type HandyoutEventAudience = "owner" | "public";

export interface UseHandyoutEventsOptions {
  audience?: HandyoutEventAudience;
}

function streamUrl(handyoutId: string, audience: HandyoutEventAudience): string {
  return audience === "public"
    ? `/api/events/handyout/${handyoutId}/public`
    : `/api/events/handyout/${handyoutId}`;
}

export function subscribeToHandyoutEvents(
  handyoutId: string,
  subscriber: HandyoutEventSubscriber,
  audience: HandyoutEventAudience = "owner",
): () => void {
  return subscribeToEventStream(streamUrl(handyoutId, audience), {
    onEvent: (event) => subscriber.onEvent(event as HandyoutEvent),
    onResync: () => subscriber.onResync?.(),
  });
}

/** One EventSource per handyout, shared by every subscriber in the tab. */
export function useHandyoutEvents(
  handyoutId: string | undefined,
  subscriber: HandyoutEventSubscriber,
  { audience = "owner" }: UseHandyoutEventsOptions = {},
) {
  const latest = useAsRef(subscriber);

  useEffect(() => {
    if (!handyoutId) return;
    return subscribeToHandyoutEvents(
      handyoutId,
      {
        onEvent: (event) => latest.current.onEvent(event),
        onResync: () => latest.current.onResync?.(),
      },
      audience,
    );
  }, [handyoutId, latest, audience]);
}
